// import { createSlice } from '@reduxjs/toolkit';

// interface AttendanceState {
//   attendance: unknown[];
//   loading: boolean;
//   error: string | null;
// }

// const initialState: AttendanceState = {
//   attendance: [],
//   loading: false,
//   error: null,
// };

// const attendanceSlice = createSlice({
//   name: 'attendance',
//   initialState,
//   reducers: {
//     setAttendance: (state, action) => {
//       state.attendance = action.payload;
//     },
//     setLoading: (state, action) => {
//       state.loading = action.payload;
//     },
//     setError: (state, action) => {
//       state.error = action.payload;
//     },
//   },
// });

// export const { setAttendance, setLoading, setError } = attendanceSlice.actions;
// export default attendanceSlice.reducer;
// src/features/academics/attendance/attendanceSlice.ts
import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "../../../redux/store";

import { nowIsoString } from "@/lib/date";

/** Minimal types */
export type AttendanceStatus = "present" | "absent" | "late" | "excused";

export interface AttendanceRecord {
  id: string; // e.g. `${studentId}_${date}`
  studentId: string;
  studentName: string;
  classId?: string;
  className?: string;
  date: string; // YYYY-MM-DD
  status: AttendanceStatus;
  remarks?: string;
  updatedAt?: string;
}

interface AttendanceState {
  attendance: AttendanceRecord[];
  loading: boolean;
  error: string | null;
}

const initialState: AttendanceState = {
  attendance: [],
  loading: false,
  error: null,
};

/**
 * Minimal API layer placeholders.
 * Replace these with your real HTTP calls (axios/fetch).
 */
const api = {
  fetchAttendance: async (_date: string, _classId?: string) => {
    void _date;
    void _classId;
    // Replace with: return axios.get('/attendance', { params: { date, classId }});
    return Promise.resolve<AttendanceRecord[]>([]); // empty by default
  },
  saveAttendance: async (records: AttendanceRecord[]) => {
    // Replace with: return axios.post('/attendance/bulk', records);
    return Promise.resolve(records.map((r) => ({ ...r, updatedAt: nowIsoString() })));
  },
  patchAttendance: async (record: AttendanceRecord) => {
    // Replace with: return axios.patch(`/attendance/${record.id}`, record);
    return Promise.resolve({ ...record, updatedAt: nowIsoString() });
  },
};

/** Thunks (minimal) */
export const fetchAttendance = createAsyncThunk<
  AttendanceRecord[],
  { date: string; classId?: string },
  { rejectValue: string }
>("attendance/fetch", async ({ date, classId }, { rejectWithValue }) => {
  try {
    const res = await api.fetchAttendance(date, classId);
    return res;
  } catch (err: unknown) {
    return rejectWithValue(
      err instanceof Error ? err.message : "Failed to fetch attendance",
    );
  }
});

export const saveAttendance = createAsyncThunk<
  AttendanceRecord[],
  AttendanceRecord[],
  { rejectValue: string }
>("attendance/save", async (records, { rejectWithValue }) => {
  try {
    const res = await api.saveAttendance(records);
    return res;
  } catch (err: unknown) {
    return rejectWithValue(
      err instanceof Error ? err.message : "Failed to save attendance",
    );
  }
});

export const patchAttendance = createAsyncThunk<
  AttendanceRecord,
  AttendanceRecord,
  { rejectValue: string }
>("attendance/patch", async (record, { rejectWithValue }) => {
  try {
    const res = await api.patchAttendance(record);
    return res;
  } catch (err: unknown) {
    return rejectWithValue(
      err instanceof Error ? err.message : "Failed to update attendance",
    );
  }
});

/** Slice */
const attendanceSlice = createSlice({
  name: "attendance",
  initialState,
  reducers: {
    // cheap helpers if you need quick local updates
    setAttendance(state, action: PayloadAction<AttendanceRecord[]>) {
      state.attendance = action.payload;
    },
    clearAttendance(state) {
      state.attendance = [];
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // fetch
    builder.addCase(fetchAttendance.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAttendance.fulfilled, (state, action) => {
      // replace attendance for the requested date(s)
      // minimal: just replace the whole array (UI should fetch per date/filter)
      state.attendance = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchAttendance.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? "Failed to fetch";
    });

    // save bulk
    builder.addCase(saveAttendance.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(saveAttendance.fulfilled, (state, action) => {
      // upsert returned records
      action.payload.forEach((rec) => {
        const idx = state.attendance.findIndex((a) => a.id === rec.id);
        if (idx === -1) state.attendance.push(rec);
        else state.attendance[idx] = rec;
      });
      state.loading = false;
    });
    builder.addCase(saveAttendance.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? "Failed to save";
    });

    // patch single
    builder.addCase(patchAttendance.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(patchAttendance.fulfilled, (state, action) => {
      const rec = action.payload;
      const idx = state.attendance.findIndex((a) => a.id === rec.id);
      if (idx === -1) state.attendance.push(rec);
      else state.attendance[idx] = rec;
      state.loading = false;
    });
    builder.addCase(patchAttendance.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? "Failed to update";
    });
  },
});

export const { setAttendance, clearAttendance } = attendanceSlice.actions;
export default attendanceSlice.reducer;

/** Selectors (minimal) */
export const selectAttendance = (state: RootState) => state.attendance.attendance;
export const selectAttendanceLoading = (state: RootState) => state.attendance.loading;
export const selectAttendanceError = (state: RootState) => state.attendance.error;

/** small helper selector for date (YYYY-MM-DD) */
export const selectAttendanceForDate = (date: string) => (state: RootState) =>
  state.attendance.attendance.filter((a) => a.date === date);
